import * as React from 'react';
import { useRef, useState } from 'react';
import {
  defaultFilterData,
  RowsDataContext,
  defaultRowsData,
  DataMap,
  DataMapItem,
  LastVisitedRowIndexMap,
  ColumnInfo,
  FieldValuesFilterData,
} from './types';
import { DataSortVO } from '@mc/data-filter-react';
import { DataAggregateVO, DataGroupVO } from '../final-table/FinalTableProps';

export const RowsDataProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [_dataMap, setDataMap] = useState<DataMap>({});
  const dataMapRef = useRef<DataMap>(_dataMap);
  dataMapRef.current = _dataMap;
  const dataMapLastVisitedRef = useRef<LastVisitedRowIndexMap>({});
  const getVal = (key: string): DataMapItem => {
    const val = { ...dataMapRef.current[key] } || {};

    if (!val.rowsData) {
      val.rowsData = defaultRowsData;
    }
    if (!val.filterData) {
      val.filterData = defaultFilterData;
    }

    return val;
  };

  const setVal = (key: string, item: DataMapItem) => {
    let newMap = {
      ...dataMapRef.current,
      [key]: item,
    };
    dataMapRef.current = newMap;
    setDataMap(newMap);
  };

  const setFieldValuesFilter = (
    key: string,
    fieldValuesFilter: FieldValuesFilterData
  ) => {
    let val = getVal(key);
    val.filterData = {
      ...val.filterData,
      fieldValuesFilter,
      lastFilterChange: new Date().getMilliseconds(),
    };

    // Při změně filtru nulujeme vybraná id
    val.rowsData = {
      ...val.rowsData,
      checkedIds: [],
    };

    setVal(key, val);
  };

  const setFilter = (key: string, formFilter: any, queryFilter: any) => {
    let val = getVal(key);
    if (val.paginationData) {
      val.paginationData.page = 1;
    }
    val.filterData = {
      ...val.filterData,
      formFilter,
      queryFilter,
      lastFilterChange: new Date().getMilliseconds(),
    };

    // Při změně filtru nulujeme vybraná id
    val.rowsData = {
      ...val.rowsData,
      checkedIds: [],
    };

    setVal(key, val);
  };

  const setVisibleFields = (key: string, fields: string[]) => {
    let val = getVal(key);

    val.filterData = {
      ...val.filterData,
      visibleFields: fields,
    };

    setVal(key, val);
  };
  const setLastVisitedRowIndex = (key: string, lastVisitedRowIndex: number) => {
    dataMapLastVisitedRef.current[key] = lastVisitedRowIndex;
  };

  const setShowFooter = (key: string, showFooter: boolean) => {
    let val = getVal(key);
    val.rowsData = {
      ...val.rowsData,
      showFooter,
    };

    setVal(key, val);
  };

  const setSort = (key: string, sort: DataSortVO[]) => {
    let val = getVal(key);
    val.paginationData.page = 1;
    val.rowsData = {
      ...val.rowsData,
      sort,
    };

    setVal(key, val);
  };

  const setColumns = (key: string, columns: ColumnInfo[]) => {
    let val = getVal(key);
    val.rowsData = {
      ...val.rowsData,
      columns,
    };

    setVal(key, val);
  };

  const setGrouping = (key: string, grouping: DataGroupVO[]) => {
    let val = getVal(key);
    val.rowsData = {
      ...val.rowsData,
      grouping,
    };

    setVal(key, val);
  };

  const setAggregate = (key: string, aggregate: DataAggregateVO[]) => {
    let val = getVal(key);
    val.rowsData = {
      ...val.rowsData,
      aggregate,
    };

    setVal(key, val);
  };

  const setScrollTop = (key: string, scrollTop: number) => {
    let val = getVal(key);

    val.rowsData = {
      ...val.rowsData,
      scrollTop,
    };

    setVal(key, val);
  };

  const setCheckedIds = (key: string, checkedIds: any[]) => {
    let val = getVal(key);

    val.rowsData = {
      ...val.rowsData,
      checkedIds,
    };

    setVal(key, val);
  };
  const setPage = (key: string, page: number) => {
    let val = getVal(key);

    val.paginationData = {
      ...val.paginationData,
      page,
    };

    setVal(key, val);
  };

  const setGroupColumnWidth = (key: string, groupColumnWidth: number) => {
    let val = getVal(key);

    val.rowsData = {
      ...val.rowsData,
      groupColumnWidth,
    };

    setVal(key, val);
  };

  return (
    <RowsDataContext.Provider
      value={{
        dataMap: dataMapRef.current,
        dataMapLastVisitedRowIndex: dataMapLastVisitedRef.current,
        setVisibleFields,
        setFilter,
        setFieldValuesFilter,
        setColumns,
        setSort,
        setShowFooter,
        setScrollTop,
        setCheckedIds,
        setPage,
        setLastVisitedRowIndex,
        setGrouping,
        setAggregate,
        setGroupColumnWidth,
        initByProvider: true,
      }}
    >
      {children}
    </RowsDataContext.Provider>
  );
};
